import { computed, reactive } from 'vue';
import { useLocaleStore } from '@/stores/useLocaleStore';
import { publicMessages } from '@/i18n/publicMessages';

const loadedMessages = reactive({
  de: publicMessages.de,
  en: publicMessages.en
});

let messagesLoadPromise = null;

const mergeDeep = (target, source) => {
  if (!source || typeof source !== 'object') return target;
  Object.entries(source).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      target[key] = value.slice();
      return;
    }
    if (value && typeof value === 'object') {
      const base = target[key] && typeof target[key] === 'object' ? target[key] : {};
      target[key] = mergeDeep({ ...base }, value);
      return;
    }
    target[key] = value;
  });
  return target;
};

export const ensureI18nMessagesLoaded = async () => {
  if (messagesLoadPromise) return messagesLoadPromise;
  messagesLoadPromise = import('@/i18n/messages').then(({ messages }) => {
    loadedMessages.de = mergeDeep({ ...loadedMessages.de }, messages.de);
    loadedMessages.en = mergeDeep({ ...loadedMessages.en }, messages.en);
    return loadedMessages;
  });
  return messagesLoadPromise;
};

const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return String(path)
    .split('.')
    .reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj);
};

const interpolate = (str, params) => {
  if (typeof str !== 'string') return str;
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => {
    const v = params[k];
    return v === undefined || v === null ? '' : String(v);
  });
};

export const useI18n = () => {
  const localeStore = useLocaleStore();

  const locale = computed(() => localeStore.locale);

  const t = (key, params) => {
    const l = localeStore.locale || 'de';
    const fallback = 'de';

    const val =
      getByPath(loadedMessages?.[l], key) ??
      getByPath(loadedMessages?.[fallback], key) ??
      key;

    return interpolate(val, params);
  };

  return { locale, setLocale: localeStore.setLocale, toggleLocale: localeStore.toggle, t };
};
